import { FC, useRef, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "motion/react";
import { IMDSSessionProps } from "@/mds/components/sessions/session.types";
import { MDSCombobox, MDSComboboxSubmitHandler, MDSComboboxVariant } from "@/mds/components/forms";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import { MDSSessionContentRenderer } from "@/mds/components/sessions/session-content/session-content-renderer";
import {
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import { SessionArtifactViewer } from "@/mds/components/sessions/session-parts/artifacts/viewer";
import { useMDSSession } from "@/mds/components/sessions/session-context/mds-session-context";
import { MDSSessionCountdownTimer } from "@/mds/components/sessions/session-content/countdown-timer";
import {
  StyledSessionTitle,
  StyledTitleAndStatusContainer,
  StyledTitleText,
  StyledSubtitleText,
} from "@/mds/components/sessions/session.style";
import { extractCollectionMentions, extractNoteMentions } from "./session-content.utils";
import { MDSTextVariant } from "@/mds/components/text";
import { MDSBanner } from "@/mds/components/banner/banner";
import { MDSBannerKind, MDSBannerSlotName } from "@/mds/components/banner/banner.types";
import { MDSIconKind } from "@/mds/components/icon";
import { useSessionFeature } from "../session-context";
import { SessionFeature, SessionStatus } from "../session.types";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons";

interface Props extends IMDSSessionProps {}

export const MDSSessionContentDynamic: FC<Props> = ({
  sessionTitle,
  sessionSubtitle,
  comboboxProps,
  handlers: { handleFetchComboboxData, handleInputFormSubmit, handleNewSession },
}) => {
  const {
    activeArtifact,
    sessionViewMode,
    countdownDurationMs,
    isCountdownPaused,
    isCountdownVisible,
    isInputFormVisible,
    isSessionTitleVisible,
    isSessionViewExpanded,
    onCountdownComplete,
    handleHoverExtension,
    handleUnhoverExtension,
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    stopCountdown,
    expandFromNotification,
    expandFromPreview,
    sessionStatus,
    setComboboxInputRef,
  } = useMDSSession();

  const isNewChatButtonEnabled = useSessionFeature(
    SessionFeature.ContextLimitExceededNewChatButton
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const comboboxInputRef = useRef<MemCommonRichTextInputInstance>(null);

  // Focus the combobox when switching to Standard mode
  useEffect(() => {
    if (sessionViewMode === SessionViewMode.Standard && isInputFormVisible) {
      // Small delay to ensure the component is fully rendered
      const timeoutId = setTimeout(() => {
        comboboxInputRef.current?.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [sessionViewMode, isInputFormVisible]);

  // Set the combobox input ref in the session context when the component mounts and updates
  useEffect(() => {
    setComboboxInputRef(comboboxInputRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboboxInputRef.current]);

  const handleComboboxSubmit: MDSComboboxSubmitHandler = ({ mdxContent, mentions }) => {
    stopCountdown();

    // TODO - Clean this up.
    const mentionedCollections = extractCollectionMentions(mentions);
    const mentionedNotes = extractNoteMentions(mentions);

    return handleInputFormSubmit({
      content: mdxContent,
      context: {
        // TODO - Clean this up.
        mentions: [...mentionedCollections, ...mentionedNotes],
      },
    });
  };

  // Caller focus handlers compose with the session context's own — spreading
  // them onto the combobox directly would replace it and break isInputFocused.
  const {
    onInputFocus: onCallerInputFocus,
    onInputBlur: onCallerInputBlur,
    ...comboboxOverrides
  } = comboboxProps ?? ({} as NonNullable<IMDSSessionProps["comboboxProps"]>);

  const isMinimized = sessionViewMode === SessionViewMode.Minimized;
  const isNotificationMode = sessionViewMode === SessionViewMode.Notification;
  const isPreviewMode = sessionViewMode === SessionViewMode.Preview;
  const isContextLimitExceeded = sessionStatus === SessionStatus.ContextLimitExceeded;

  const [forceCloseCombobox, setForceCloseCombobox] = useState(false);

  const handleContainerClick = (_e: React.MouseEvent) => {
    if (isNotificationMode) {
      expandFromNotification();
      return;
    }

    if (isPreviewMode) {
      expandFromPreview();
      return;
    }

    // Close combobox when clicking anywhere in the container
    setForceCloseCombobox(true);
    // Reset the flag after a brief delay to allow the combobox to process the close
    setTimeout(() => setForceCloseCombobox(false), 100);
  };

  return (
    <AnimatePresence mode="wait">
      {!isMinimized && (
        <ResponsiveContainer
          ref={containerRef}
          isExpanded={isSessionViewExpanded}
          onMouseEnter={handleHoverExtension}
          onMouseLeave={handleUnhoverExtension}
          onClick={handleContainerClick}
          style={{
            cursor: isNotificationMode || isPreviewMode ? "pointer" : "default",
          }}
          initial={{ opacity: 0, height: "auto" }}
          animate={{
            opacity: 1,
            height: "auto",
            transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          }}
          exit={{
            opacity: 0,
            transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          <ResponsiveContentContainer isExpanded={isSessionViewExpanded}>
            <AnimatedContainer
              isExpanded={isSessionViewExpanded}
              animate={{
                height: "100%",
              }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              {isContextLimitExceeded && (
                <MDSBanner
                  kind={MDSBannerKind.Info}
                  iconKind={MDSIconKind.InfoCircle}
                  message="This chat has gotten too long"
                >
                  {isNewChatButtonEnabled && (
                    <MDSButton
                      variant={MDSButtonVariant.Danger}
                      slot={MDSBannerSlotName.Right}
                      onClick={handleNewSession}
                    >
                      New chat
                    </MDSButton>
                  )}
                </MDSBanner>
              )}

              {isCountdownVisible && (
                <MDSSessionCountdownTimer
                  isPaused={isCountdownPaused}
                  durationMs={countdownDurationMs}
                  onComplete={onCountdownComplete}
                />
              )}

              {isSessionTitleVisible && (
                <StyledSessionTitle variant={MDSTextVariant.HeaderS}>
                  <StyledTitleAndStatusContainer>
                    <StyledTitleText>{sessionTitle}</StyledTitleText>
                  </StyledTitleAndStatusContainer>
                  <StyledSubtitleText>{sessionSubtitle}</StyledSubtitleText>
                </StyledSessionTitle>
              )}

              <SessionArtifactViewer isVisible={!!activeArtifact} />

              <MDSSessionContentRenderer mode={sessionViewMode} />

              {isInputFormVisible && (
                <StyledSection>
                  <StyledForm
                    onSubmit={e => {
                      e.preventDefault();
                    }}
                  >
                    <MDSCombobox
                      placeholder="Ask or tell Mem something"
                      fetchData={handleFetchComboboxData}
                      variant={MDSComboboxVariant.Brand}
                      onSubmit={handleComboboxSubmit}
                      popoverProps={{
                        isNonModal: true,
                        style: {
                          maxWidth: "365px",
                        },
                      }}
                      onInputFocus={() => {
                        handleInputFocus();
                        onCallerInputFocus?.();
                      }}
                      onInputBlur={() => {
                        handleInputBlur();
                        onCallerInputBlur?.();
                      }}
                      onInputChange={handleInputChange}
                      autoFocus={sessionViewMode === SessionViewMode.Standard}
                      inputRef={comboboxInputRef}
                      mentions={[]}
                      forceClose={forceCloseCombobox}
                      isDisabled={isContextLimitExceeded}
                      {...comboboxOverrides}
                    />
                  </StyledForm>
                </StyledSection>
              )}
            </AnimatedContainer>
          </ResponsiveContentContainer>
        </ResponsiveContainer>
      )}
    </AnimatePresence>
  );
};

const ResponsiveContainer = styled(motion.section)<{ isExpanded: boolean }>`
  width: ${({ isExpanded }) => (isExpanded ? "400px" : "min-content")};
  min-width: ${({ isExpanded }) => (isExpanded ? "400px" : "min-content")};
  height: 100%;
  border: 1px solid #e0e0e0;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  background: ${({ theme }) => theme.colors.surface.chat};
  border-radius: 16px;
  max-height: 800px;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.2),
    0 8px 16px rgba(0, 0, 0, 0.1);
  backface-visibility: hidden;
`;

const ResponsiveContentContainer = styled.div<{ isExpanded: boolean }>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const AnimatedContainer = styled(motion.div)<{ isExpanded: boolean }>`
  height: 100%;
  width: 100%;
  will-change: height;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
`;

export const StyledSection = styled.section`
  width: 100%;
  padding-left: ${({ theme }) => `${theme.spacing[5]}`};
  padding-right: ${({ theme }) => `${theme.spacing[5]}`};
  padding-bottom: ${({ theme }) => `${theme.spacing[5]}`};
  padding-top: 8px;
  min-height: 60px;
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 0;
`;
